import { FunctionComponent, useEffect, useState } from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { Autocomplete, TextField } from '@mui/material';
import { CreateTransactionModalFormInput, CreateTransactionModalFormModel } from '../CreateTransactionModal/CreateTransactionModalFormModel';
import { useTranslation } from '@/lib';
import { locationService } from '@/modules/locations/api/locations/location.service';
import { LocationModel } from '@/modules/locations/types/LocationModel';
import { LocationType } from '@/modules/locations/api/locations/location.contracts';

interface TransactionCarrierFieldProps {
  control: Control<CreateTransactionModalFormInput, unknown, CreateTransactionModalFormModel>;
  errors: FieldErrors<CreateTransactionModalFormInput>;
  disabled?: boolean;
}

export const TransactionCarrierField: FunctionComponent<TransactionCarrierFieldProps> = ({ control, errors, disabled }) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<LocationModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const loadOptions = async (query: string) => {
    setIsLoading(true);
    try {
      const response = await locationService.getByQueryAndType({ query, type: LocationType.CARRIER });
      if (response.isSuccess) {
        setOptions(response.payload);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadOptions('');
  }, []);

  return (
    <Controller
      name="carrierId"
      control={control}
      render={({ field }) => {
        const [selectedOption, setSelectedOption] = useState<LocationModel | null>(null);

        useEffect(() => {
          if (field.value && typeof field.value === 'number') {
            const loadLocationById = async () => {
              const locationResponse = await locationService.getById(field.value as number);
              if (locationResponse.isSuccess) {
                setSelectedOption(locationResponse.payload);
              }
            };
            loadLocationById();
          } else {
            setSelectedOption(null);
          }
        }, [field.value]);

        return (
          <Autocomplete
            value={selectedOption}
            inputValue={inputValue}
            options={options}
            onOpen={() => loadOptions('')}
            isOptionEqualToValue={(option, value) => option.dto.id === value.dto.id}
            getOptionLabel={(option) => `${option.dto.name} (${option.dto.code})`}
            onInputChange={(event, newInputValue) => {
              loadOptions(newInputValue);
              setInputValue(newInputValue);
            }}
            filterOptions={(x) => x}
            onChange={(_, value) => {
              setSelectedOption(value);
              field.onChange(value?.dto.id);
            }}
            loading={isLoading}
            disabled={disabled}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('carrier')}
                error={errors.carrierId !== undefined}
                helperText={errors.carrierId?.message}
                slotProps={{
                  ...params.slotProps,
                  inputLabel: { shrink: true },
                }}
              />
            )}
            renderOption={(props, option) => {
              return (
                <li {...props} key={option.dto.id}>
                  {option.dto.name} ({option.dto.code})
                </li>
              );
            }}
          />
        );
      }}
    />
  );
};
